// src/pages/Stats.jsx
import React, { useEffect, useState } from "react";
import useTrafficData from "../hooks/useTrafficData";
import usePlateSearch from "../hooks/usePlateSearch";
import DaypartChips from "../features/stats/DaypartChips";
import CameraChips from "../features/stats/CameraChips";
import PlateFilter from "../features/stats/PlateFilter";
import TodayCard from "../features/stats/TodayCard";
import MonthCard from "../features/stats/MonthCard";
import YearCard from "../features/stats/YearCard";
import { firstOfMonth } from "../lib/dates";
import { CHART_COLORS } from "../lib/chartTheme"; // themed colors for charts

// small local helper (used only for export filename prefixes)
const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function Stats() {
  // global filters
  // bucket: "", "morning", "afternoon", "evening", "overnight"
  const [bucket, setBucket] = useState("");
  // sunMode: null = off, 1 = Dusk–Dawn (night), 0 = Dawn–Dusk (day)
  const [sunMode, setSunMode] = useState(null);

  // plate search state via hook
  const plateSearch = usePlateSearch();
  const plate = plateSearch.plate;

  // cameras visibility
  const [visible, setVisible] = useState({ cam41: true, chel: true, pool: true });
  const toggleCam = (key) =>
    setVisible((v) => {
      const count = (v.cam41 ? 1 : 0) + (v.chel ? 1 : 0) + (v.pool ? 1 : 0);
      if (v[key] && count === 1) return v; // prevent unchecking the last camera
      return { ...v, [key]: !v[key] };
    });
  const showAll = () => setVisible({ cam41: true, chel: true, pool: true });

  // per-card pickers
  const [day, setDay] = useState(() => new Date());
  const [month, setMonth] = useState(() => firstOfMonth(new Date()));
  const [year, setYear] = useState(() => new Date().getFullYear());

  // responsive: 3 columns on wide screens
  const [isWide, setIsWide] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1200 : false
  );
  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 1200);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // data
  const { todayRows, monthRows, yearRows } = useTrafficData({
    day,
    month,
    year,
    bucket,          // clock-based bucket
    sunMode,         // <-- IMPORTANT: pass sunMode, not isNight
    plate,
  });

  const cardsWrap = isWide
    ? { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, alignItems: "start" }
    : { display: "grid", gridTemplateColumns: "1fr", gap: 16 };

  // filenames for chart/table exports (cards can use these)
  const exportPrefixes = {
    today: `today_${ymd(day)}`,
    month: `month_${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`,
    year: `year_${year}`,
  };

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 16px" }}>
      <h1 style={{ fontSize: 24, color: "#e6edf3", marginBottom: 12 }}>Traffic Statistics</h1>

      {/* Global controls */}
      <DaypartChips
        bucket={bucket}
        setBucket={setBucket}
        sunMode={sunMode}
        setSunMode={setSunMode}
      />
      <CameraChips visible={visible} toggle={toggleCam} showAll={showAll} />
      <PlateFilter
        plate={plate}
        plateText={plateSearch.plateText}
        setPlateText={plateSearch.setPlateText}
        plateMatches={plateSearch.plateMatches}
        onSearch={plateSearch.runSearch}
        onClear={plateSearch.clear}
        onPick={plateSearch.applyCandidate}
      />

      {/* Cards */}
      <div style={cardsWrap}>
        <TodayCard
          day={day}
          setDay={setDay}
          visible={visible}
          rows={todayRows}
          plate={plate}
          chartColors={CHART_COLORS}
          exportPrefix={exportPrefixes.today}
        />

        <MonthCard
          month={month}
          setMonth={setMonth}
          visible={visible}
          rows={monthRows}
          plate={plate}
          setDay={setDay}
          chartColors={CHART_COLORS}
          exportPrefix={exportPrefixes.month}
        />

        <YearCard
          year={year}
          setYear={setYear}
          visible={visible}
          rows={yearRows}
          plate={plate}
          setMonth={setMonth}
          chartColors={CHART_COLORS}
          exportPrefix={exportPrefixes.year}
        />
      </div>
    </div>
  );
}
